import { Injectable, Injector } from "@angular/core";
import { ChatAudit, ChatAuditBreezeModel } from "@common/ADAPT.Common.Model/organisation/chat-audit";
import { MethodologyPredicate } from "@common/lib/data/methodology-predicate";
import { BaseService } from "@common/service/base.service";
import { AiModel, AiModelPricingByModelName } from "./ai-model-pricing";

export interface IChatAuditFilters {
    organisationId?: number;
    startDate?: Date;
    endDate?: Date;
}

@Injectable({
    providedIn: "root",
})
export class AiPricingService extends BaseService {
    private static readonly TOKENS_PER_MILLION = 1_000_000;

    public constructor(injector: Injector) {
        super(injector);
    }

    public getChatAudits(filters: IChatAuditFilters = {}) {
        const predicate = this.createChatAuditPredicate(filters);
        return this.commonDataService.getWithOptions(ChatAuditBreezeModel, predicate.getKey(), {
            predicate,
            navProperty: "organisation,person",
            forceRemote: true,
        });
    }

    public getAiModel(modelString: string) {
        // modelString is something returned from response, e.g. in the form: "gpt-5-mini-2025-08-07"
        return Object.values(AiModel).find((model) => modelString.startsWith(model));
    }

    public calculateTotalCost(chatAudit: ChatAudit): number {
        return this.calculateTotalCostForModel(
            chatAudit.modelName as AiModel,
            chatAudit.inputTokens,
            chatAudit.cachedTokens,
            chatAudit.outputTokens,
            chatAudit.reasoningTokens
        );
    }

    public calculateTotalCostForModel(modelName: AiModel, inputTokens: number, cachedTokens: number, outputTokens: number, reasoningTokens: number): number {
        const modelPricing = AiModelPricingByModelName[modelName];
        if (!modelPricing) {
            return 0;
        }

        const safeInputTokens = Math.max(inputTokens, 0);
        const safeCachedTokens = Math.max(cachedTokens, 0);
        const safeOutputTokens = Math.max(outputTokens, 0);
        const safeReasoningTokens = Math.max(reasoningTokens, 0);

        const inputTokensChargedAtCachedRate = modelPricing.costPerMillionCachedTokens > 0
            ? Math.min(safeCachedTokens, safeInputTokens)
            : 0;
        const inputTokensChargedAtInputRate = modelPricing.costPerMillionCachedTokens > 0
            ? Math.max(safeInputTokens - inputTokensChargedAtCachedRate, 0)
            : safeInputTokens;

        const outputTokensChargedAtReasoningRate = modelPricing.costPerMillionReasoningTokens > 0
            ? Math.min(safeReasoningTokens, safeOutputTokens)
            : 0;
        const outputTokensChargedAtOutputRate = modelPricing.costPerMillionReasoningTokens > 0
            ? Math.max(safeOutputTokens - outputTokensChargedAtReasoningRate, 0)
            : safeOutputTokens;

        const inputCost = (inputTokensChargedAtInputRate / AiPricingService.TOKENS_PER_MILLION) * modelPricing.costPerMillionInputTokens;
        const cachedCost = (inputTokensChargedAtCachedRate / AiPricingService.TOKENS_PER_MILLION) * modelPricing.costPerMillionCachedTokens;
        const outputCost = (outputTokensChargedAtOutputRate / AiPricingService.TOKENS_PER_MILLION) * modelPricing.costPerMillionOutputTokens;
        const reasoningCost = (outputTokensChargedAtReasoningRate / AiPricingService.TOKENS_PER_MILLION) * modelPricing.costPerMillionReasoningTokens;

        return inputCost + cachedCost + outputCost + reasoningCost;
    }

    public calculateTotalCostForPerson(personId: number, chatAudits: ChatAudit[]): number {
        return chatAudits
            .filter((chatAudit) => chatAudit.personId === personId)
            .reduce((totalCost, chatAudit) => totalCost + this.calculateTotalCost(chatAudit), 0);
    }

    public calculateTotalCostForOrganisation(organisationId: number, chatAudits: ChatAudit[]): number {
        return chatAudits
            .filter((chatAudit) => chatAudit.organisationId === organisationId)
            .reduce((totalCost, chatAudit) => totalCost + this.calculateTotalCost(chatAudit), 0);
    }

    private createChatAuditPredicate(filters: IChatAuditFilters) {
        const predicate = new MethodologyPredicate<ChatAudit>();

        if (filters.organisationId != null) {
            predicate.and(new MethodologyPredicate<ChatAudit>("organisationId", "==", filters.organisationId));
        }

        if (filters.startDate) {
            predicate.and(new MethodologyPredicate<ChatAudit>("startTime", ">=", filters.startDate));
        }

        if (filters.endDate) {
            predicate.and(new MethodologyPredicate<ChatAudit>("startTime", "<", this.addDays(filters.endDate, 1)));
        }

        return predicate;
    }

    private addDays(date: Date, numberOfDays: number) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate() + numberOfDays);
    }
}
